const PROMO_TRIGGER_SELECTOR = '[data-element="first-order-promo-trigger"]';
const NEWSLETTER_POPUP_SELECTOR = 'newsletter-popup';
const FLOATING_BUTTON_SELECTOR = '[data-element="floating-button"]';
const FLOATING_BUTTON_TRIGGER_SELECTOR = '.\\#newsletter-popup-floating-button-trigger';
const SHOPIFY_FORMS_EMBED_SELECTOR = 'shopify-forms-embed';
const SHOPIFY_FORMS_TEASER_SELECTOR = 'button[aria-haspopup="dialog"]';
const OPEN_RETRY_DELAY_MS = 250;
const OPEN_RETRY_LIMIT = 8;

let openRetryTimer = null;

const getNewsletterPopup = () => document.querySelector(NEWSLETTER_POPUP_SELECTOR);
const getShopifyFormsEmbed = () => document.querySelector(SHOPIFY_FORMS_EMBED_SELECTOR);
const getShopifyFormsTeaserButton = () =>
    getShopifyFormsEmbed()?.shadowRoot?.querySelector(SHOPIFY_FORMS_TEASER_SELECTOR) || null;

const isVisible = element => {
  if (!element || element.hidden) {
    return false;
  }

  return !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
};

const isShopifyFormsFormOpen = () => {
  const embed = getShopifyFormsEmbed();
  const shadow = embed?.shadowRoot;
  const app = shadow?.querySelector('#app-embed');

  if (!app || app.getAttribute('data-current-step') !== 'form') {
    return false;
  }

  const formModal = shadow?.querySelector(
    'section[aria-modal="true"][class*="formContainer"], section[role="dialog"][class*="formContainer"]'
  );

  return !!(formModal && isVisible(formModal));
};

const clearOpenRetry = () => {
  if (openRetryTimer) {
    clearTimeout(openRetryTimer);
    openRetryTimer = null;
  }
};

const getVisibleFloatingButton = () => {
  const buttons = Array.from(document.querySelectorAll(FLOATING_BUTTON_SELECTOR));

  return buttons.find(button => {
    if (!isVisible(button)) {
      return false;
    }

    return button.textContent?.includes('%');
  }) || null;
};

const openOriginalFloatingButton = () => {
  const floatingButton = getVisibleFloatingButton();

  if (!floatingButton) {
    return false;
  }

  const floatingButtonTrigger =
      floatingButton.querySelector(FLOATING_BUTTON_TRIGGER_SELECTOR) || floatingButton;

  floatingButtonTrigger.dispatchEvent(
      new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window
      })
  );

  return true;
};

const extractDiscountValue = value => {
  const match = value?.match(/(\d+)\s*%/);

  return match?.[1] || '';
};

const clearShopifyFormsCache = () => {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('shopify_forms_dismissed') || key.includes('shopify_forms_state') || key.includes('forms_visit_count'))) {
        localStorage.removeItem(key);
      }
    }
  } catch (e) {
    console.warn('[ProductPricePromo] Failed to clear Shopify Forms cache:', e);
  }
};

const hijackShopifyFormsClose = () => {
  const embed = getShopifyFormsEmbed();
  if (!embed || !embed.shadowRoot || embed.__hijacked) {
    return;
  }

  const shadow = embed.shadowRoot;
  embed.__hijacked = true;

  // Use capture: true to intercept the click before it reaches the app's internal listeners
  shadow.addEventListener('click', (event) => {
    const path = event.composedPath();
    
    // Identify if the click is on something that looks like a close button
    const isCloseAction = path.some(node => {
      if (node.nodeType !== 1) return false;
      
      // The teaser button itself has aria-haspopup="dialog". Close buttons do not.
      if (node.getAttribute('aria-haspopup') === 'dialog') return false;

      const label = (node.getAttribute('aria-label') || '').toLowerCase();
      const title = (node.getAttribute('title') || '').toLowerCase();
      const className = (typeof node.className === 'string' ? node.className : '').toLowerCase();
      const isButton = node.tagName === 'BUTTON' || node.tagName === 'A' || node.getAttribute('role') === 'button';
      
      return isButton && (
        label.includes('close') || 
        label.includes('dismiss') || 
        label.includes('hide') ||
        title.includes('close') ||
        className.includes('close')
      );
    });

    if (isCloseAction) {
      const teaserButton = getShopifyFormsTeaserButton();
      // Look for the teaser wrapper. Usually it has 'teaser' in its class.
      // If not, we take the immediate parent of the teaser button.
      const teaserContainer = teaserButton?.closest?.('[class*="teaser" i]') || teaserButton?.parentElement;
      
      // Determine if the click happened inside the teaser's container
      const isInsideTeaser = teaserContainer && path.includes(teaserContainer);

      if (isInsideTeaser) {
        console.log('[ProductPricePromo] Intercepted teaser close action');
        event.stopImmediatePropagation();
        event.preventDefault();

        // Hide the teaser container
        teaserContainer.style.transition = 'opacity 0.3s ease';
        teaserContainer.style.opacity = '0';
        teaserContainer.style.visibility = 'hidden';
        teaserContainer.style.pointerEvents = 'none';
        
        // Mark as manually hidden so our opener knows to show it
        embed.setAttribute('data-manually-hidden', 'true');
      } else {
        // If it's not inside the teaser (e.g. it's the Modal/Form), let it close normally.
        console.log('[ProductPricePromo] Close action outside teaser detected, allowing default behavior');
      }
    }
  }, true); // Important: capture phase
};

const openShopifyFormsTeaser = () => {
  if (isShopifyFormsFormOpen()) {
    return true;
  }

  const teaserButton = getShopifyFormsTeaserButton();
  const embed = getShopifyFormsEmbed();

  if (!teaserButton) {
    return false;
  }

  // If we manually hidden it, restore it first
  if (embed && embed.getAttribute('data-manually-hidden') === 'true') {
    const container = teaserButton.closest('[class*="teaser" i]') || teaserButton;
    container.style.opacity = '1';
    container.style.visibility = 'visible';
    container.style.pointerEvents = 'auto';
    embed.removeAttribute('data-manually-hidden');
  }

  if (!isVisible(teaserButton)) {
    // If it's still not visible (e.g. app's own internal hidden state), we can't click it
    return false;
  }

  teaserButton.click();
  return true;
};

const getDiscountValue = trigger => {
  const popup = getNewsletterPopup();
  const popupDiscount = popup?.dataset?.promoDiscount?.trim();

  if (popupDiscount) {
    return popupDiscount;
  }

  const teaserDiscount = extractDiscountValue(
      getShopifyFormsTeaserButton()?.getAttribute('aria-label') ||
      getShopifyFormsTeaserButton()?.textContent ||
      ''
  );

  if (teaserDiscount) {
    return teaserDiscount;
  }

  return trigger?.dataset?.fallbackDiscount?.trim() || '';
};

const syncTrigger = trigger => {
  const discountValue = getDiscountValue(trigger);

  if (!discountValue) {
    return;
  }

  const nextText = `get ${discountValue}% off`;

  if (trigger.textContent === nextText) {
    return;
  }

  trigger.textContent = nextText;
};

const syncAllTriggers = () => {
  document.querySelectorAll(PROMO_TRIGGER_SELECTOR).forEach(syncTrigger);
};

const openNewsletterPopup = (attempt = 0) => {
  if (isShopifyFormsFormOpen()) {
    clearOpenRetry();
    return;
  }

  if (openOriginalFloatingButton()) {
    clearOpenRetry();
    return;
  }

  if (openShopifyFormsTeaser()) {
    clearOpenRetry();
    return;
  }

  if (attempt >= OPEN_RETRY_LIMIT) {
    clearOpenRetry();
    return;
  }

  clearOpenRetry();
  openRetryTimer = window.setTimeout(() => {
    openNewsletterPopup(attempt + 1);
  }, OPEN_RETRY_DELAY_MS);
};

if (!window.__productPricePromoInitialized) {
  document.addEventListener('click', event => {
    const trigger = event.target.closest(PROMO_TRIGGER_SELECTOR);

    if (!trigger) {
      return;
    }

    event.preventDefault();
    openNewsletterPopup();
  });

  const observer = new MutationObserver(() => {
    syncAllTriggers();
    hijackShopifyFormsClose(); // Try to hijack whenever DOM changes
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  // Wait a bit after load to ensure App is hydrated
  window.addEventListener('load', () => {
      setTimeout(hijackShopifyFormsClose, 2000);
      setInterval(hijackShopifyFormsClose, 5000);
  });

  window.__productPricePromoInitialized = true;
}

syncAllTriggers();
hijackShopifyFormsClose();
